/**
 * BusinessWizardV2 — premium 3-column wizard z live preview dla profili firmowych.
 *
 * Architektura analogiczna do KennelWizardV2, ale dla `profile_type === 'business'`.
 * Kroki:
 *   1. Podstawy firmy (nazwa, branża, lokalizacja)
 *   2. O firmie (opis + krótki opis)
 *   3. Oferta (pakiety usług)
 *   4. Portfolio / Realizacje
 *   5. Opinie klientów
 *   6. Kontakt
 *   7. Gotowe (publikacja)
 *
 * Backend: POST /api/business/, PUT /api/business/{id}.
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Eye, X, ChevronLeft, ChevronRight, Check, Save, ArrowRight, Globe, Inbox, LayoutGrid } from 'lucide-react';
import { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { firePawConfetti } from '@/utils/confetti';
import { haptics } from '@/utils/haptics';

import StudioShell from '@/components/studio/StudioShell';
import { Progress } from '@/components/ui/progress';
import WizardSidebar from '@/pages/KennelWizardV2/WizardSidebar';
import LivePreview from './LivePreview';
import PublishSuccessFlow from '@/components/wizard/PublishSuccessFlow';
import { WizardTopbar } from '@/components/wizard/WizardTopbar';
import { WizardMobileBar } from '@/components/wizard/WizardMobileBar';

import Step1Basics from './steps/Step1Basics';
import Step2About from './steps/Step2About';
import Step3Services from './steps/Step3Services';
import Step4Portfolio from './steps/Step4Portfolio';
import Step5Reviews from './steps/Step5Reviews';
import Step6Contact from './steps/Step6Contact';
import Step7Done from './steps/Step7Done';

const STEPS = [
  { id: 1, name: 'Podstawy firmy', subtitle: 'Nazwa, branża, lokalizacja', Component: Step1Basics },
  { id: 2, name: 'O firmie', subtitle: 'Opowiedz o sobie', Component: Step2About },
  { id: 3, name: 'Oferta', subtitle: 'Pakiety usług i cennik', Component: Step3Services },
  { id: 4, name: 'Portfolio', subtitle: 'Realizacje i zdjęcia', Component: Step4Portfolio },
  { id: 5, name: 'Opinie klientów', subtitle: 'Buduj wiarygodność', Component: Step5Reviews },
  { id: 6, name: 'Kontakt', subtitle: 'Dane kontaktowe i social', Component: Step6Contact },
  { id: 7, name: 'Gotowe!', subtitle: 'Opublikuj i zobacz efekty', Component: Step7Done },
];

const DEFAULT_FORM = {
  name: '',
  industry: '',
  postal_code: '',
  city: '',
  address: '',
  description: '',
  short_description: '',
  services: [],
  portfolio: [],
  reviews: [],
  cover_photo: '',
  logo: '',
  photos: [],
  phone: '',
  email: '',
  website: '',
  opening_hours: '',
  social_facebook: '',
  social_instagram: '',
  accent_color: 'gold',
  theme: 'dark',
  show_in_catalog: true,
  font_style: 'elegant',
};

function validateStep(stepId, data) {
  const errors = {};
  if (stepId === 1) {
    if (!data.name?.trim() || data.name.trim().length < 3) errors.name = 'Podaj nazwę firmy (min. 3 znaki)';
    if (!data.industry) errors.industry = 'Wybierz branżę';
    if (!data.city?.trim()) errors.city = 'Podaj miasto';
  }
  return errors;
}

const DRAFT_KEY = 'apisai_business_wizard_v2_draft';

export default function BusinessWizardV2() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();

  // Deep-link: ?krok=<klucz> → otwórz konkretny krok kreatora (np. „Usługi" z menu).
  const KROK_MAP = { podstawy: 1, ofirmie: 2, oferta: 3, uslugi: 3, portfolio: 4, opinie: 5, kontakt: 6 };
  const krokStep = KROK_MAP[(searchParams.get('krok') || '').toLowerCase()] || null;

  const [step, setStep] = useState(krokStep || 1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [publishedHandoff, setPublishedHandoff] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState('');
  // drawerOpen state removed — StudioShell handles its own mobile drawer
  const setDrawerOpen = () => {}; void setDrawerOpen;
  const [businessId, setBusinessId] = useState(id || null);

  // Load existing business or draft
  useEffect(() => {
    if (!isEdit) {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft?.form) {
            const mergedForm = { ...DEFAULT_FORM, ...draft.form };
            setForm(mergedForm);
            setCompletedSteps(draft.completedSteps || []);
            // NIE przywracaj wprost na krok „Podsumowanie" z pustymi danymi (chyba że deep-link ?krok=).
            let restoredStep = krokStep || draft.step || 1;
            if (!krokStep) {
              const step1Invalid = Object.keys(validateStep(1, mergedForm)).length > 0;
              if (step1Invalid) restoredStep = 1;
              else if (restoredStep >= STEPS.length) restoredStep = STEPS.length - 1;
            }
            setStep(restoredStep);
          }
        }
      } catch {}
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/business/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const b = res.data;
        setForm({
          ...DEFAULT_FORM,
          name: b.name || '',
          industry: b.industry || '',
          postal_code: b.postal_code || '',
          city: b.city || '',
          address: b.address || '',
          description: b.description || '',
          short_description: b.short_description || '',
          services: b.services || [],
          portfolio: b.portfolio || [],
          reviews: b.reviews || [],
          cover_photo: b.cover_photo || '',
          logo: b.logo || '',
          photos: b.photos || [],
          phone: b.phone || '',
          email: b.email || '',
          website: b.website || '',
          opening_hours: b.opening_hours || '',
          social_facebook: b.social_facebook || '',
          social_instagram: b.social_instagram || '',
          accent_color: b.accent_color || 'gold',
          theme: b.theme || 'dark',
          font_style: b.font_style || 'elegant',
        });
        setCompletedSteps([1, 2, 3, 4, 5, 6]);
      } catch {
        toast.error('Nie udało się wczytać firmy');
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [isEdit, id, token, krokStep]);

  // Autosave draft
  useEffect(() => {
    if (isEdit) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, completedSteps }));
    } catch {}
  }, [form, step, completedSteps, isEdit]);

  // SWIPE-BACK gesture (mobile only)
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 1024) return;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let tracking = false;

    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      if (t.clientX > 30) return;
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      tracking = true;
    };
    const onEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      const dt = Date.now() - startTime;
      if (dx > 100 && dy < 60 && dt < 600 && step > 1 && !submitting) {
        haptics.tap();
        setStep((s) => Math.max(s - 1, 1));
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [step, submitting]);

  const updateData = (patch) => {
    setForm((p) => ({ ...p, ...patch }));
    setErrors({});
  };

  const current = STEPS.find((s) => s.id === step);
  const StepComponent = current?.Component;
  const progressPct = useMemo(() => Math.round((step / STEPS.length) * 100), [step]);
  const isLastStep = step === STEPS.length;

  const goToStep = (newStep) => {
    // Wolna nawigacja — user może klikać dowolny krok (backward + forward).
    setStep(newStep);
  };

  const handleNext = () => {
    if (isLastStep) {
      handlePublish();
      return;
    }
    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length > 0) {
      haptics.error();
      setErrors(stepErrors);
      toast.error('Uzupełnij wymagane pola');
      return;
    }
    haptics.tap();
    setErrors({});
    setCompletedSteps((prev) => Array.from(new Set([...prev, step])));
    setStep(step + 1);
  };

  const persist = async (extra = {}) => {
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { ...form, ...extra };
    if (businessId) {
      const res = await axios.put(`${API}/business/${businessId}`, payload, { headers });
      return res.data;
    }
    const res = await axios.post(`${API}/business/`, payload, { headers });
    setBusinessId(res.data.id);
    return res.data;
  };

  const handleSaveAndExit = async () => {
    if (!form.name?.trim() || !form.industry) {
      toast.success('Szkic zapisany. Możesz wrócić w każdej chwili.');
      setTimeout(() => navigate('/panel'), 600);
      return;
    }
    setSubmitting(true);
    try {
      await persist({ is_published: false });
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      toast.success('Zapisano. Możesz wrócić w każdej chwili.');
      setTimeout(() => navigate('/panel'), 600);
    } catch (e) {
      toast.warning('Nie udało się zapisać na serwerze — szkic został zachowany lokalnie.');
      setTimeout(() => navigate('/panel'), 800);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    const stepErrors = validateStep(1, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast.error('Uzupełnij wymagane pola w kroku 1');
      setStep(1);
      return;
    }
    if (!termsAccepted && !isEdit) {
      toast.error('Zaakceptuj regulamin');
      return;
    }

    setSubmitting(true);
    try {
      const result = await persist({ is_published: true });
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      firePawConfetti();
      toast.success(isEdit ? 'Firma zaktualizowana' : 'Firma opublikowana!');
      // AUTO-PROGRESSION: ujednolicony Ekran Sukcesu zamiast cichego redirectu
      setPublishedSlug(result?.slug || form.slug || '');
      setPublishedHandoff(true);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Nie udało się zapisać';
      toast.error(typeof detail === 'string' ? detail : 'Błąd zapisu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const wizardTopbarSlot = (
    <WizardTopbar
      prefix="business-wizard-v2"
      step={step}
      totalSteps={STEPS.length}
      stepName={current?.name}
      progressPct={progressPct}
      isLastStep={isLastStep}
      submitting={submitting}
      nextDisabled={submitting || (isLastStep && !termsAccepted && !isEdit)}
      publishLabel="Opublikuj"
      onSaveExit={handleSaveAndExit}
      onNext={handleNext}
      viewHref={form?.slug ? `/firma/${form.slug}` : undefined}
    />
  );

  return (
    <StudioShell
      mode="workspace"
      contextLabel="Tworzenie profilu firmy"
      topbarSlot={wizardTopbarSlot}
      activeKey="page"
      sidebarMuted={true}
      backTo="/panel"
      backLabel="Wróć do panelu"
    >
      <div data-testid="business-wizard-v2">
      {publishedHandoff && (
        <PublishSuccessFlow
          entityType="firma"
          slug={publishedSlug}
          userName={user?.name}
          entityName={form.name}
          coverImage={form.cover_photo || form.logo || (form.photos && form.photos[0])}
          subtitle={form.city}
          onClose={() => { setPublishedHandoff(false); navigate('/panel'); }}
          onNavigate={(path) => { setPublishedHandoff(false); navigate(path); }}
          nextItems={[
            { key: 'leads', icon: Inbox, label: 'Przejdź do zapytań', path: '/panel/zapytania' },
            { key: 'panel', icon: LayoutGrid, label: 'Wróć do panelu', path: '/panel' },
          ]}
        />
      )}

      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-32 lg:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">
          {!isLastStep && (
          <WizardSidebar
            steps={STEPS}
            currentStep={step}
            completedSteps={completedSteps}
            onStepClick={goToStep}
          />
          )}

          <section className={`flex-1 min-w-0 w-full ${isLastStep ? '' : 'apisai-soft-card p-5 sm:p-7'}`} data-testid="business-wizard-v2-form-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.18 }}
              >
                {StepComponent && (
                  <StepComponent
                    data={form}
                    updateData={updateData}
                    errors={errors}
                    termsAccepted={termsAccepted}
                    setTermsAccepted={setTermsAccepted}
                    goToStep={goToStep}
                    onPublish={handlePublish}
                    onSaveLater={handleSaveAndExit}
                    submitting={submitting}
                    isEdit={isEdit}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </section>

          {/* PRAWA KOLUMNA: PODGLĄD NA ŻYWO — widoczny też na kroku Podsumowanie (układ „Birch Haus") */}
          <section className="hidden lg:block w-full lg:w-[420px] xl:w-[460px] flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <LivePreview data={form} />
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE: wspólny WizardMobileBar (pływający podgląd + dolny pasek akcji + arkusz) */}
      <WizardMobileBar
        prefix="business-wizard-v2"
        step={step}
        totalSteps={STEPS.length}
        isLastStep={isLastStep}
        submitting={submitting}
        nextDisabled={submitting || (isLastStep && !termsAccepted && !isEdit)}
        publishLabel="Opublikuj"
        onBack={() => { if (step > 1) { haptics.tap(); setStep(step - 1); } }}
        onSaveExit={handleSaveAndExit}
        onNext={handleNext}
        previewNode={<LivePreview data={form} />}
      />
      </div>
    </StudioShell>
  );
}
